import React from 'react';
import SEO from '../components/SEO';

const Privacy: React.FC = () => {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Privacy Policy',
    description: 'Privacy Policy for JackpotQuantum.com - Learn how we collect, use, and protect your personal information.',
  };

  return (
    <>
      <SEO
        title="Privacy Policy - JackpotQuantum.com"
        description="Read our Privacy Policy to understand how JackpotQuantum.com collects, uses, and protects your personal information when you use our website."
        canonical="/privacy"
        jsonLd={jsonLd}
      />

      <div className="min-h-screen bg-dark-900">
        <section className="py-20 bg-gradient-to-br from-dark-800 to-dark-900">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-5xl md:text-6xl font-bold mb-6">
                <span className="text-gradient">Privacy</span> <span className="text-white">Policy</span>
              </h1>
              <p className="text-gray-400">Last updated: {new Date().toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
            </div>
          </div>
        </section>

        <section className="py-20 bg-dark-800">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto glass rounded-2xl p-8 md:p-12">
              <div className="prose prose-invert max-w-none">
                <h2 className="text-3xl font-bold text-white mb-6">1. Introduction</h2>
                <p className="text-gray-300 leading-relaxed mb-6">
                  Welcome to JackpotQuantum.com ("we," "our," or "us"). We are committed to protecting your privacy and ensuring 
                  you have a positive experience on our website. This Privacy Policy explains how we collect, use, disclose, and 
                  safeguard your information when you visit our website.
                </p>

                <h2 className="text-3xl font-bold text-white mb-6 mt-12">2. Information We Collect</h2>
                <h3 className="text-2xl font-semibold text-white mb-4 mt-8">2.1 Information You Provide</h3>
                <p className="text-gray-300 leading-relaxed mb-4">
                  We may collect information that you voluntarily provide to us when you:
                </p>
                <ul className="list-disc list-inside text-gray-300 space-y-2 mb-6 ml-4">
                  <li>Subscribe to our newsletter</li>
                  <li>Contact us through our contact forms</li>
                  <li>Participate in surveys or promotions</li>
                  <li>Leave comments or reviews</li>
                </ul>

                <h3 className="text-2xl font-semibold text-white mb-4 mt-8">2.2 Automatically Collected Information</h3>
                <p className="text-gray-300 leading-relaxed mb-4">
                  When you visit our website, we automatically collect certain information about your device, including:
                </p>
                <ul className="list-disc list-inside text-gray-300 space-y-2 mb-6 ml-4">
                  <li>IP address and location data</li>
                  <li>Browser type and version</li>
                  <li>Operating system</li>
                  <li>Pages you visit and time spent on pages</li>
                  <li>Referring website addresses</li>
                  <li>Device identifiers</li>
                </ul>

                <h2 className="text-3xl font-bold text-white mb-6 mt-12">3. How We Use Your Information</h2>
                <p className="text-gray-300 leading-relaxed mb-4">
                  We use the information we collect to:
                </p>
                <ul className="list-disc list-inside text-gray-300 space-y-2 mb-6 ml-4">
                  <li>Provide, maintain, and improve our services</li>
                  <li>Personalize your experience on our website</li>
                  <li>Send you newsletters and promotional communications (with your consent)</li>
                  <li>Respond to your inquiries and provide customer support</li>
                  <li>Analyze website usage and trends</li>
                  <li>Detect, prevent, and address technical issues</li>
                  <li>Comply with legal obligations</li>
                </ul>

                <h2 className="text-3xl font-bold text-white mb-6 mt-12">4. Cookies and Tracking Technologies</h2>
                <p className="text-gray-300 leading-relaxed mb-6">
                  We use cookies and similar tracking technologies to track activity on our website and store certain information. 
                  Cookies are files with a small amount of data which may include an anonymous unique identifier. You can instruct 
                  your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, 
                  you may not be able to use some portions of our website. For more information, please see our{' '}
                  <a href="/cookies" className="text-quantum-400 hover:underline">Cookie Policy</a>.
                </p>

                <h2 className="text-3xl font-bold text-white mb-6 mt-12">5. Third-Party Services</h2>
                <p className="text-gray-300 leading-relaxed mb-6">
                  We may use third-party services that collect, monitor, and analyze information to help us improve our website. 
                  These services may include:
                </p>
                <ul className="list-disc list-inside text-gray-300 space-y-2 mb-6 ml-4">
                  <li>Google Analytics for website analytics</li>
                  <li>Affiliate tracking systems for commission tracking</li>
                  <li>Email marketing platforms for newsletter management</li>
                  <li>Social media platforms for content sharing</li>
                </ul>
                <p className="text-gray-300 leading-relaxed mb-6">
                  These third parties have access to your information only to perform specific tasks on our behalf and are obligated 
                  not to disclose or use it for any other purpose.
                </p>

                <h2 className="text-3xl font-bold text-white mb-6 mt-12">6. Data Security</h2>
                <p className="text-gray-300 leading-relaxed mb-6">
                  We implement appropriate technical and organizational security measures to protect your personal information against 
                  unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet or 
                  electronic storage is 100% secure, and we cannot guarantee absolute security.
                </p>

                <h2 className="text-3xl font-bold text-white mb-6 mt-12">7. Your Rights</h2>
                <p className="text-gray-300 leading-relaxed mb-4">
                  Under UK GDPR, you have the following rights regarding your personal information:
                </p>
                <ul className="list-disc list-inside text-gray-300 space-y-2 mb-6 ml-4">
                  <li><strong className="text-white">Right to Access:</strong> You can request copies of your personal data</li>
                  <li><strong className="text-white">Right to Rectification:</strong> You can request correction of inaccurate data</li>
                  <li><strong className="text-white">Right to Erasure:</strong> You can request deletion of your personal data</li>
                  <li><strong className="text-white">Right to Restrict Processing:</strong> You can request limitation of data processing</li>
                  <li><strong className="text-white">Right to Data Portability:</strong> You can request transfer of your data</li>
                  <li><strong className="text-white">Right to Object:</strong> You can object to processing of your personal data</li>
                </ul>
                <p className="text-gray-300 leading-relaxed mb-6">
                  To exercise these rights, please contact us using the information provided in the "Contact Us" section below.
                </p>

                <h2 className="text-3xl font-bold text-white mb-6 mt-12">8. Data Retention</h2>
                <p className="text-gray-300 leading-relaxed mb-6">
                  We will retain your personal information only for as long as necessary to fulfill the purposes outlined in this 
                  Privacy Policy, unless a longer retention period is required or permitted by law. When we no longer need your 
                  personal information, we will securely delete or anonymize it.
                </p>

                <h2 className="text-3xl font-bold text-white mb-6 mt-12">9. Children's Privacy</h2>
                <p className="text-gray-300 leading-relaxed mb-6">
                  Our website is not intended for individuals under the age of 18. We do not knowingly collect personal information 
                  from children. If you are a parent or guardian and believe your child has provided us with personal information, 
                  please contact us immediately.
                </p>

                <h2 className="text-3xl font-bold text-white mb-6 mt-12">10. Changes to This Privacy Policy</h2>
                <p className="text-gray-300 leading-relaxed mb-6">
                  We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy 
                  Policy on this page and updating the "Last updated" date. You are advised to review this Privacy Policy periodically 
                  for any changes.
                </p>

                <h2 className="text-3xl font-bold text-white mb-6 mt-12">11. Contact Us</h2>
                <p className="text-gray-300 leading-relaxed mb-4">
                  If you have any questions about this Privacy Policy, please contact us:
                </p>
                <div className="bg-dark-700 rounded-lg p-6 mb-6">
                  <p className="text-gray-300">
                    <strong className="text-white">Email:</strong> privacy@jackpotquantum.com<br />
                    <strong className="text-white">Website:</strong> jackpotquantum.com
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Privacy;

